import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, Outlet } from 'react-router-dom';
import ErrorBoundary from '@/components/ErrorBoundary';

// Public pages — lightweight, lazy-loaded only when visited
const About = lazy(() => import('@/pages/About'));
const Access = lazy(() => import('@/pages/Access'));
const DevLogin = lazy(() => import('@/pages/DevLogin'));
const DevLanding = lazy(() => import('@/pages/DevLanding'));

// Authenticated shell — Auth0 + Redux + Toaster, loaded on first protected navigation
const AuthenticatedApp = lazy(() =>
  import('@/components/AuthenticatedApp').then((m) => ({ default: m.AuthenticatedApp }))
);

// Protected pages — lazy-loaded, only fetched when the route matches
const Photos = lazy(() => import('@/pages/Photos'));
const Albums = lazy(() => import('@/pages/Albums'));
const AlbumDetail = lazy(() => import('@/pages/AlbumDetail'));
const PhotoDetail = lazy(() => import('@/pages/PhotoDetail'));
const Trash = lazy(() => import('@/pages/Trash'));
const Settings = lazy(() => import('@/pages/Settings'));
const SharedAlbums = lazy(() => import('@/pages/SharedAlbums'));
const AlbumInvites = lazy(() => import('@/pages/AlbumInvites'));
const SharedAlbumDetail = lazy(() => import('@/pages/SharedAlbumDetail'));

// Lazy-loaded supporting components for the authenticated shell
const ProtectedRoute = lazy(() =>
  import('@/components/auth/ProtectedRoute').then((m) => ({ default: m.ProtectedRoute }))
);
const Layout = lazy(() =>
  import('@/components/layout/Layout').then((m) => ({ default: m.Layout }))
);

/** Layout route: mounts Auth0 + Redux once for all authenticated routes */
function AuthenticatedShell() {
  return (
    <AuthenticatedApp>
      <Outlet />
    </AuthenticatedApp>
  );
}

/** Layout route: mounts auth guard + layout once for all protected routes */
function ProtectedShell() {
  return (
    <ProtectedRoute>
      <Layout>
        <Outlet />
      </Layout>
    </ProtectedRoute>
  );
}

/** Root route: DevLanding on localhost (dev tool), redirect to /access elsewhere. */
function RootRoute() {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocalhost) return <DevLanding />;
  return <Navigate to="/access" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Suspense fallback={null}>
          <Routes>
            {/* Public routes — no providers needed. `/` branches on hostname:
                localhost → DevLanding (dev tool), else → redirect to /access. */}
            <Route path="/" element={<RootRoute />} />
            <Route path="/about" element={<About />} />
            <Route path="/dev-login" element={<DevLogin />} />

            {/* Authenticated shell — Auth0 + Redux mounted once for all child routes */}
            <Route element={<AuthenticatedShell />}>
              {/* Access needs auth providers but NOT ProtectedRoute */}
              <Route path="/access" element={<Access />} />

              {/* Protected routes — auth guard + layout, providers shared from parent */}
              <Route element={<ProtectedShell />}>
                <Route path="/photos" element={<Photos />} />
                <Route path="/albums" element={<Albums />} />
                <Route path="/albums/:id" element={<AlbumDetail />} />
                <Route path="/albums/:albumId/photos/:photoId" element={<PhotoDetail />} />
                <Route path="/shared" element={<SharedAlbums />} />
                <Route path="/shared/invites" element={<AlbumInvites />} />
                <Route path="/shared/:id" element={<SharedAlbumDetail />} />
                <Route path="/shared/:albumId/photos/:photoId" element={<PhotoDetail />} />
                <Route path="/photos/:id" element={<PhotoDetail />} />
                <Route path="/trash" element={<Trash />} />
                <Route path="/settings" element={<Settings />} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
